/** @module @liquidefy/code-editor/circuits/circuit-types @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 CIRCUIT HANDLE — base interface for all circuits
// ═══════════════════════════════════════════════════════════════════════════════

/** Health status for a circuit. */
export type CircuitHealth = 'healthy' | 'degraded' | 'unhealthy' | 'destroyed';

/** Health report returned by every circuit. */
export interface CircuitHealthReport {
  readonly name: string;
  readonly health: CircuitHealth;
  readonly details: string;
  readonly checkedAt: number;
  readonly uptime: number;
}

/** Base interface every circuit handle must implement. */
export interface CircuitHandle {
  readonly name: string;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §2 CIRCUIT STATS — universal stats tracking
// ═══════════════════════════════════════════════════════════════════════════════

/** Universal stats every circuit tracks. */
export interface CircuitStats {
  readonly totalCalls: number;
  readonly successCount: number;
  readonly errorCount: number;
  readonly lastCallAt: number | null;
  readonly createdAt: number;
  readonly uptime: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 CIRCUIT TIER — metadata for circuit classification
// ═══════════════════════════════════════════════════════════════════════════════

/** Circuit tier classification. */
export type CircuitTier = 1 | 2 | 3 | 4 | 5;

/** Circuit metadata for registry/discovery. */
export interface CircuitMetadata {
  readonly name: string;
  readonly tier: CircuitTier;
  readonly description: string;
  readonly domains: readonly string[];
  readonly version: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 HELPERS — shared utilities for circuit implementations
// ═══════════════════════════════════════════════════════════════════════════════

/** Create a health report. */
export function buildHealthReport(
  name: string,
  health: CircuitHealth,
  details: string,
  createdAt: number
): CircuitHealthReport {
  return {
    name,
    health,
    details,
    checkedAt: Date.now(),
    uptime: Date.now() - createdAt,
  };
}

/** Create initial circuit stats. */
export function emptyStats(): CircuitStats {
  const now = Date.now();
  return {
    totalCalls: 0,
    successCount: 0,
    errorCount: 0,
    lastCallAt: null,
    createdAt: now,
    uptime: 0,
  };
}

/** Record a successful call. */
export function recordSuccess(stats: CircuitStats): CircuitStats {
  return {
    ...stats,
    totalCalls: stats.totalCalls + 1,
    successCount: stats.successCount + 1,
    lastCallAt: Date.now(),
    uptime: Date.now() - stats.createdAt,
  };
}

/** Record a failed call. */
export function recordError(stats: CircuitStats): CircuitStats {
  return {
    ...stats,
    totalCalls: stats.totalCalls + 1,
    errorCount: stats.errorCount + 1,
    lastCallAt: Date.now(),
    uptime: Date.now() - stats.createdAt,
  };
}

/** Compute health from stats. */
export function healthFromStats(stats: CircuitStats): CircuitHealth {
  if (stats.totalCalls === 0) return 'healthy';
  const errorRate = stats.errorCount / stats.totalCalls;
  if (errorRate > 0.5) return 'unhealthy';
  if (errorRate > 0.2) return 'degraded';
  return 'healthy';
}

/** Format stats as a details string. */
export function formatStatsDetails(stats: CircuitStats): string {
  return `Calls: ${stats.totalCalls}, Success: ${stats.successCount}, Errors: ${stats.errorCount}`;
}
